import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DELETE_CALENDAR_EVENT_MUTATION, SET_CALENDAR_EVENT_MUTATION, STORES_QUERY } from '../graphql/queries';
import { useCalendarEvents, type CalendarEvent } from '../hooks/useCalendarEvents';
import { gql } from '../lib/api';

interface Store { id: string; name: string }

const dateInJapan = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const dateString = (date: Date) => date.toISOString().slice(0, 10);
const changeMonth = (month: string, step: number) => {
  const [year, number] = month.split('-').map(Number);
  return dateString(new Date(Date.UTC(year, number - 1 + step, 1))).slice(0, 7);
};
const monthEnd = (month: string) => {
  const [year, number] = month.split('-').map(Number);
  return dateString(new Date(Date.UTC(year, number, 0)));
};
const labelDate = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return `${year}年${String(month).padStart(2, '0')}月${String(day).padStart(2, '0')}日`;
};

export const CalendarEventsPage = ({ isAdmin }: { isAdmin: boolean }) => {
  const today = useMemo(dateInJapan, []);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedStore, setSelectedStore] = useState('all');
  const [title, setTitle] = useState('イベント');
  const [message, setMessage] = useState('');
  const qc = useQueryClient();
  const storesQ = useQuery({ queryKey: ['stores'], queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY) });
  const eventsQ = useCalendarEvents(`${month}-01`, monthEnd(month));
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data]);
  const events = useMemo(() => eventsQ.data?.calendarEvents ?? [], [eventsQ.data]);
  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((event) => map.set(event.date, [...(map.get(event.date) ?? []), event]));
    return map;
  }, [events]);
  const selectedEvents = byDate.get(selectedDate) ?? [];
  const [year, monthNumber] = month.split('-').map(Number);
  const firstWeekday = (new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay() + 6) % 7;
  const daysInMonth = Number(monthEnd(month).slice(-2));
  const calendarDays = Array.from({ length: Math.ceil((firstWeekday + daysInMonth) / 7) * 7 }, (_, index) => {
    const day = index - firstWeekday + 1;
    return day >= 1 && day <= daysInMonth ? `${month}-${String(day).padStart(2, '0')}` : '';
  });

  const save = useMutation({
    mutationFn: async () => {
      const targetIds = selectedStore === 'all' ? stores.map((store) => store.id) : [selectedStore];
      for (const storeId of targetIds) {
        await gql(SET_CALENDAR_EVENT_MUTATION, { storeId, date: selectedDate, title: title.trim() });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['calendarEvents'] });
      setMessage(`${labelDate(selectedDate)}のイベントを登録しました。`);
    },
    onError: () => setMessage('登録できませんでした。内容を確認して再度お試しください。'),
  });
  const remove = useMutation({
    mutationFn: (event: CalendarEvent) => gql(DELETE_CALENDAR_EVENT_MUTATION, { storeId: event.storeId, date: event.date }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['calendarEvents'] });
      setMessage('イベントを削除しました。');
    },
    onError: () => setMessage('削除できませんでした。再度お試しください。'),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage('');
    if (title.trim() && title.trim().length <= 100 && selectedStore && stores.length) save.mutate();
  };

  return (
    <div className="calendar-page">
      <header className="calendar-heading">
        <div><span>OPERATIONS / CALENDAR</span><h1>イベント</h1><p>特別な営業日を記録し、通常営業のKPIと分けて比較します。</p></div>
        <div className="calendar-heading-mark" aria-hidden="true"><b>{monthNumber.toString().padStart(2, '0')}</b><small>{year}</small></div>
      </header>
      <div className="calendar-explain"><strong>KPI除外日</strong><p>登録した店舗・日付は週間売上、ドリンク分析、滞在時間と人数、座席稼働率ヒートマップの集計から除外します。売上などの元データは削除されません。両店開催の場合は両店に登録できます。</p></div>
      <div className="calendar-layout">
        <section className="calendar-board" aria-label={`${year}年${monthNumber}月のイベントカレンダー`}>
          <div className="calendar-toolbar"><div><span>EVENT CALENDAR</span><h2>{year}年 {String(monthNumber).padStart(2, '0')}月</h2></div><div className="calendar-nav"><button type="button" onClick={() => setMonth(changeMonth(month, -1))} aria-label="前の月">←</button><button type="button" onClick={() => { setMonth(today.slice(0, 7)); setSelectedDate(today); }}>今月</button><button type="button" onClick={() => setMonth(changeMonth(month, 1))} aria-label="次の月">→</button></div></div>
          <div className="calendar-grid" role="grid">
            {['月', '火', '水', '木', '金', '土', '日'].map((day) => <div className="calendar-weekday" key={day}>{day}</div>)}
            {calendarDays.map((date, index) => date ? (
              <button type="button" key={date} className={`calendar-day${date === selectedDate ? ' calendar-day-selected' : ''}${date === today ? ' calendar-day-today' : ''}${byDate.has(date) ? ' calendar-day-event' : ''}`} onClick={() => setSelectedDate(date)} aria-label={`${labelDate(date)}、${(byDate.get(date) ?? []).map((item) => stores.find((store) => store.id === item.storeId)?.name ?? item.storeId).join('・') || 'イベントなし'}`} aria-pressed={date === selectedDate}>
                <span>{Number(date.slice(-2))}</span><small>{(byDate.get(date) ?? []).map((item) => stores.find((store) => store.id === item.storeId)?.name ?? item.storeId).join(' / ')}</small>
              </button>
            ) : <div className="calendar-day-blank" key={`blank-${index}`} />)}
          </div>
          <div className="calendar-legend"><span><i />イベント日</span><span>月曜は定休日</span></div>
        </section>
        <aside className="calendar-detail">
          <span className="calendar-detail-kicker">SELECTED DATE</span>
          <h2>{labelDate(selectedDate)}</h2>
          <p className="calendar-detail-weekday">{['日', '月', '火', '水', '木', '金', '土'][new Date(`${selectedDate}T00:00:00Z`).getUTCDay()]}曜日</p>
          <div className="calendar-event-list">
            <h3>登録済みイベント <span>{selectedEvents.length}</span></h3>
            {selectedEvents.length === 0 && <p className="calendar-empty">この日の登録はありません。</p>}
            {selectedEvents.map((item) => <div className="calendar-event-row" key={item.storeId}><div><span>{stores.find((store) => store.id === item.storeId)?.name ?? item.storeId}</span><strong>{item.title}</strong><small>KPIから除外</small></div>{isAdmin && <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(item)} aria-label={`${item.title}（${item.storeId}）を削除`}>削除</button>}</div>)}
          </div>
          {isAdmin && <form className="calendar-form" onSubmit={submit}>
            <h3>イベント日を登録</h3>
            <label>日付<input type="date" value={selectedDate} onChange={(event) => { setSelectedDate(event.target.value); setMonth(event.target.value.slice(0, 7)); }} required /></label>
            <label>対象店舗<select value={selectedStore} onChange={(event) => setSelectedStore(event.target.value)}><option value="all">両店</option>{stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
            <label>イベント名<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={100} required placeholder="例：周年イベント" /></label>
            <button type="submit" disabled={save.isPending || !stores.length}>{save.isPending ? '登録中…' : 'イベントを登録・更新'}</button>
          </form>}
          {message && <p className="calendar-message" role="status">{message}</p>}
          {(eventsQ.isError || storesQ.isError) && <p className="calendar-error" role="alert">カレンダーデータを取得できませんでした。</p>}
        </aside>
      </div>
    </div>
  );
};
